'use client';

import { useLanguage, type UILocale } from './LanguageContext';
import { looksLikeRawI18nKey, safeTranslate } from './safeTranslation';
import en from './messages/en.json';
import fr from './messages/fr.json';
import id from './messages/id.json';
import tl from './messages/tl.json';
import es from './messages/es.json';
import de from './messages/de.json';
import zh from './messages/zh.json';
import hi from './messages/hi.json';

type Messages = typeof en;

const MESSAGES: Record<UILocale, Messages> = {
  en,
  fr: fr as unknown as Messages,
  id: id as unknown as Messages,
  tl: tl as unknown as Messages,
  es: es as unknown as Messages,
  de: de as unknown as Messages,
  zh: zh as unknown as Messages,
  hi: hi as unknown as Messages,
};

type PathsToLeaves<T> = T extends string
  ? []
  : {
      [K in keyof T & string]: [K, ...PathsToLeaves<T[K]>];
    }[keyof T & string];

type Join<T extends string[], D extends string = '.'> = T extends []
  ? never
  : T extends [infer F extends string]
  ? F
  : T extends [infer F extends string, ...infer R extends string[]]
  ? `${F}${D}${Join<R, D>}`
  : string;

export type TranslationKey = Join<PathsToLeaves<Messages>>;

function get(obj: Record<string, unknown>, path: string): string {
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') return path;
    cur = (cur as Record<string, unknown>)[part];
  }
  return typeof cur === 'string' ? cur : path;
}

export function useTranslation() {
  const { locale } = useLanguage();
  const messages = MESSAGES[locale] as unknown as Record<string, unknown>;

  function t(key: TranslationKey, vars?: Record<string, string>): string {
    let text = get(messages, key);
    // Fall back to English if key is missing in the current locale
    if (text === key) {
      const fallback = get(MESSAGES.en as unknown as Record<string, unknown>, key);
      text = fallback !== key ? fallback : key;
    }
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        text = text.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
      });
    }
    return text;
  }

  function safeT(key: TranslationKey, fallback: string, vars?: Record<string, string>): string {
    return safeTranslate(t, key, fallback, vars);
  }

  return { t, safeT, locale, looksLikeRawI18nKey };
}

export { looksLikeRawI18nKey, safeTranslate };
